import MapView from "@arcgis/core/views/MapView";
import { loadMultipleModules } from "./LazyloadMap";

/**
 * Apple Maps basemap for the consumer's ArcGIS MapView. Apple has no public
 * tile endpoint (see rooftop3d_epc's appleMapKit.ts), so a display-only
 * MapKit JS map is drawn in a div behind the MapView, whose own basemap is
 * empty, and kept on the same extent/rotation as the view.
 */

declare global {
  interface Window {
    mapkit: any;
  }
}

export const APPLE_BASEMAP_CONTAINER_ID = "consumerAppleBasemap";

const MAPKIT_JS_SRC = "https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js";
const MAPKIT_INIT_TIMEOUT_MS = 15_000;
// Half the Web Mercator world width, in meters (EPSG:3857).
const MERCATOR_HALF = 20037508.342789244;

interface TokenCandidate {
  name: string;
  token: string | undefined;
}

const decodeClaims = (token: string): { origin?: string; exp?: number } | null => {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
};

/** Whether a MapKit `origin` claim (comma-separated, "*." wildcards) allows this page. */
const originAllowed = (originClaim: string | undefined, pageOrigin: string) => {
  if (!originClaim) return true;
  const page = new URL(pageOrigin);
  return originClaim.split(",").map((o) => o.trim()).filter(Boolean).some((pattern) => {
    if (pattern === "*") return true;
    const hasScheme = /^[a-z]+:\/\//i.test(pattern);
    if (hasScheme && !pattern.toLowerCase().startsWith(`${page.protocol}//`)) return false;
    const hostPattern = (hasScheme ? pattern.replace(/^[a-z]+:\/\//i, "") : pattern).replace(/\/+$/, "").toLowerCase();
    const host = hostPattern.includes(":") ? page.host.toLowerCase() : page.hostname.toLowerCase();
    if (hostPattern.startsWith("*.")) return host.endsWith(hostPattern.slice(1));
    return host === hostPattern;
  });
};

// The _BUILD token is origin-restricted (*.pvnxt.com) and non-expiring; the
// dev token works anywhere but expires. Built bundles prefer _BUILD.
const pickToken = () => {
  const dev: TokenCandidate = { name: "REACT_APP_APPLE_MAPKIT_TOKEN", token: process.env.REACT_APP_APPLE_MAPKIT_TOKEN };
  const build: TokenCandidate = { name: "REACT_APP_APPLE_MAPKIT_TOKEN_BUILD", token: process.env.REACT_APP_APPLE_MAPKIT_TOKEN_BUILD };
  const candidates = process.env.NODE_ENV === "production" ? [build, dev] : [dev, build];
  const problems: string[] = [];
  for (const { name, token } of candidates) {
    if (!token) { problems.push(`${name} is not set`); continue; }
    const claims = decodeClaims(token);
    if (!claims) { problems.push(`${name} is not a valid JWT`); continue; }
    if (typeof claims.exp === "number" && claims.exp <= Date.now() / 1000) { problems.push(`${name} has expired`); continue; }
    if (!originAllowed(claims.origin, window.location.origin)) { problems.push(`${name} does not allow ${window.location.origin}`); continue; }
    return { name, token };
  }
  throw new Error(`Apple Maps is not available here: ${problems.join("; ")}.`);
};

let loadPromise: Promise<any> | null = null;

const isMapKitScript = () => {
  const src = (document.currentScript as HTMLScriptElement | null)?.src ?? "";
  return /apple-mapkit\.com|\.apple\.com\//.test(src);
};

/**
 * MapKit's scripts (mapkit.js and the chunks it loads later, e.g. when a map
 * is created) register themselves with any AMD loader they find; the ArcGIS
 * CDN build's loader then fails its next module load with "multipleDefine".
 * Wrap the page's `define` once so MapKit's own scripts see no AMD loader and
 * define nothing, while ArcGIS's modules keep using it unchanged.
 */
const shieldAmdFromMapKit = () => {
  const original = (window as any).define;
  if (typeof original !== "function" || original.__mapKitShield) return;
  const shielded: any = function (this: unknown, ...args: unknown[]) {
    if (isMapKitScript()) return undefined;
    return original.apply(this, args);
  };
  Object.keys(original).forEach((key) => { if (key !== "amd") shielded[key] = original[key]; });
  Object.defineProperty(shielded, "amd", {
    configurable: true,
    get: () => (isMapKitScript() ? undefined : original.amd),
  });
  shielded.__mapKitShield = true;
  (window as any).define = shielded;
};

/** Loads MapKit JS once and resolves when Apple accepts the token. */
const loadAppleMapKit = (): Promise<any> => {
  if (loadPromise) return loadPromise;
  shieldAmdFromMapKit();
  loadPromise = new Promise((resolve, reject) => {
    const { name: tokenName, token } = pickToken();
    if (window.mapkit?.Map && window.mapkit.__consumerInitialized) {
      resolve(window.mapkit);
      return;
    }
    const script = document.createElement("script");
    script.src = MAPKIT_JS_SRC;
    script.crossOrigin = "anonymous";
    script.async = true;
    script.onload = () => {
      const mapkit = window.mapkit;
      if (!mapkit) {
        reject(new Error("Apple MapKit JS loaded but window.mapkit is unavailable."));
        return;
      }
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error("Apple Maps did not finish initialising."));
      }, MAPKIT_INIT_TIMEOUT_MS);
      mapkit.addEventListener("configuration-change", (event: any) => {
        if (settled || (event?.status !== "Initialized" && event?.status !== "Refreshed")) return;
        settled = true;
        clearTimeout(timer);
        mapkit.__consumerInitialized = true;
        resolve(mapkit);
      });
      mapkit.addEventListener("error", (event: any) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(new Error(event?.status === "Unauthorized"
          ? `Apple rejected ${tokenName} for ${window.location.origin}.`
          : `Apple Maps error: ${event?.status ?? "unknown"}.`));
      });
      mapkit.init({ authorizationCallback: (done: (token: string) => void) => done(token) });
    };
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load Apple MapKit JS."));
    };
    document.head.appendChild(script);
  }).catch((error) => {
    // Let a later selection retry (e.g. after the dev token is refreshed).
    loadPromise = null;
    throw error;
  });
  return loadPromise;
};

let appleMap: any = null;
let syncHandle: __esri.Handle | null = null;

const syncToView = (view: MapView) => {
  const mapkit = window.mapkit;
  if (!appleMap || !mapkit || !view.center || !view.width || !view.height) return;
  // Same center and scale as the MapView, as a MapKit map rect (unit square,
  // y down). The unrotated frame is used so rotation can be applied on top.
  const center = view.center;
  const halfWidth = (view.width * view.resolution) / 2;
  const halfHeight = (view.height * view.resolution) / 2;
  const toUnit = (x: number, y: number) => [(x + MERCATOR_HALF) / (2 * MERCATOR_HALF), (MERCATOR_HALF - y) / (2 * MERCATOR_HALF)];
  const [minX, minY] = toUnit(center.x - halfWidth, center.y + halfHeight);
  const [maxX, maxY] = toUnit(center.x + halfWidth, center.y - halfHeight);
  const rotation = (360 - (view.rotation ?? 0)) % 360;
  // Only when it changes: MapKit warns on every rotation set otherwise.
  if (Math.abs((appleMap.rotation ?? 0) - rotation) > 0.01) appleMap.rotation = rotation;
  const rect = new mapkit.MapRect(minX, minY, maxX - minX, maxY - minY);
  // Jump, don't ease: MapKit's own transition made the imagery trail the
  // MapView's zoom/pan animation, so the roof seemed to fly into place.
  if (typeof appleMap.setVisibleMapRectAnimated === "function") appleMap.setVisibleMapRectAnimated(rect, false);
  else appleMap.visibleMapRect = rect;
};



// MapKit stops zooming at level 19 (camera ~264 m at this view size); past it
// the imagery stays put while the MapView -- and the roof drawn on it -- keeps
// zooming, so the roof looks oversized and zoom/drag seem dead.
const APPLE_MAX_ZOOM = 19;
let cappedView: MapView | null = null;
let maxZoomBeforeApple: number | undefined;
let lodView: MapView | null = null;
let lodsBeforeApple: any = null;

/**
 * The empty Apple basemap has no tiling scheme, so the MapView loses its zoom
 * levels (view.zoom = -1): zoom stops snapping to levels and nothing can be
 * capped. Give it the standard Web Mercator levels while Apple is shown.
 */
const keepZoomLevels = async (view: MapView) => {
  if (lodView === view) return;
  const [TileInfo] = await loadMultipleModules(["esri/layers/support/TileInfo"]);
  lodsBeforeApple = view.constraints.lods;
  lodView = view;
  view.constraints.lods = TileInfo.create({ spatialReference: { wkid: 102100 }, numLODs: 24 }).lods;
  cappedView = view;
  maxZoomBeforeApple = view.constraints.maxZoom;
  view.constraints.maxZoom = APPLE_MAX_ZOOM;
  if (view.zoom > APPLE_MAX_ZOOM) await view.goTo({ zoom: APPLE_MAX_ZOOM }, { animate: false }).catch(() => undefined);
};

// The Apple imagery can only follow the MapView, never be drawn by it the way
// Google/Esri tiles are, so any MapView animation (wheel-zoom easing, drag
// momentum) shows as the roof easing in and out over the imagery. While Apple
// is shown the MapView moves in steps instead: wheel zoom jumps one level at
// the cursor, and drags stop where the mouse stops.
const WHEEL_STEP_MS = 140;
let steppedView: MapView | null = null;
let wheelHandle: __esri.Handle | null = null;
let momentumBeforeApple: boolean | undefined;
let lastWheelStep = 0;

const stepNavigation = (view: MapView) => {
  if (steppedView === view) return;
  steppedView = view;
  momentumBeforeApple = view.navigation.momentumEnabled;
  view.navigation.momentumEnabled = false;
  wheelHandle = view.on("mouse-wheel", (event) => {
    event.stopPropagation();
    const now = Date.now();
    // A trackpad sends a burst of small wheel events per gesture: one level each.
    if (now - lastWheelStep < WHEEL_STEP_MS || !event.deltaY) return;
    lastWheelStep = now;
    const step = event.deltaY < 0 ? 1 : -1;
    const maxZoom = (view.constraints.maxZoom ?? -1) > 0 ? (view.constraints.maxZoom as number) : APPLE_MAX_ZOOM;
    const minZoom = Math.max(view.constraints.minZoom ?? 0, 0);
    const zoom = Math.min(maxZoom, Math.max(minZoom, Math.round(view.zoom) + step));
    if (zoom === Math.round(view.zoom)) return;
    // Keep the point under the cursor fixed, as wheel zoom does normally.
    const anchor = view.toMap({ x: event.x, y: event.y });
    const factor = Math.pow(2, Math.round(view.zoom) - zoom);
    const center = anchor && view.center
      ? { x: anchor.x + (view.center.x - anchor.x) * factor, y: anchor.y + (view.center.y - anchor.y) * factor, spatialReference: view.spatialReference }
      : view.center;
    void view.goTo({ center, zoom } as any, { animate: false }).catch(() => undefined);
  });
};

const restoreNavigation = () => {
  wheelHandle?.remove();
  wheelHandle = null;
  if (steppedView) {
    steppedView.navigation.momentumEnabled = momentumBeforeApple ?? true;
    steppedView = null;
  }
};

/** Show the Apple basemap behind `view`. Throws when MapKit cannot be used on this page. */
export const showAppleBasemap = async (view: MapView) => {
  const container = document.getElementById(APPLE_BASEMAP_CONTAINER_ID);
  if (!container) throw new Error("The Apple Maps container is missing.");
  const mapkit = await loadAppleMapKit();
  if (!appleMap || appleMap.element !== container) {
    appleMap?.destroy?.();
    appleMap = new mapkit.Map(container, {
      mapType: mapkit.Map.MapTypes.Hybrid,
      isZoomEnabled: false,
      isScrollEnabled: false,
      // Display-only (the container ignores the mouse); rotation must stay
      // enabled so it can follow the MapView's.
      isRotationEnabled: true,
      showsCompass: mapkit.FeatureVisibility.Hidden,
      showsZoomControl: false,
      showsMapTypeControl: false,
      showsScale: mapkit.FeatureVisibility.Hidden,
    });
  }
  await keepZoomLevels(view);
  stepNavigation(view);
  container.style.display = "block";
  syncHandle?.remove();
  // The view is from the CDN ArcGIS build, so watch it with that build's reactiveUtils.
  const [reactiveUtils] = await loadMultipleModules(["esri/core/reactiveUtils"]);
  syncHandle = reactiveUtils.watch(
    () => [view.center, view.scale, view.rotation, view.width, view.height],
    () => syncToView(view),
    // sync: follow every animation frame of the MapView, not a batched update.
    { initial: true, sync: true }
  );
};

export const hideAppleBasemap = () => {
  syncHandle?.remove();
  syncHandle = null;
  restoreNavigation();
  // Give the other basemaps their own zoom levels and full zoom range back.
  if (lodView) {
    lodView.constraints.lods = lodsBeforeApple;
    lodView = null;
    lodsBeforeApple = null;
  }
  if (cappedView) {
    cappedView.constraints.maxZoom = maxZoomBeforeApple ?? -1;
    cappedView = null;
    maxZoomBeforeApple = undefined;
  }
  const container = document.getElementById(APPLE_BASEMAP_CONTAINER_ID);
  if (container) container.style.display = "none";
};
